import { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import api from "../lib/api";

const IMG_RE = /\.(jpe?g|png|webp)$/i;
const PDF_RE = /\.pdf$/i;

export function FilePreviewButton({ file }) {
  const [open, setOpen] = useState(false);
  const isImg = IMG_RE.test(file.filename || "");
  const isPdf = PDF_RE.test(file.filename || "");
  if (!isImg && !isPdf) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`file-preview-${file.id}`}
          className="ml-3 inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 shrink-0">
          <Eye className="h-3.5 w-3.5" /> Preview
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl" data-testid="file-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-lg truncate pr-8">{file.filename}</DialogTitle>
        </DialogHeader>
        {open && <PreviewBody file={file} isPdf={isPdf} />}
      </DialogContent>
    </Dialog>
  );
}

function PreviewBody({ file, isPdf }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let objectUrl;
    api.get(`/files/${file.id}/download`, { responseType: "blob" })
      .then((r) => {
        objectUrl = URL.createObjectURL(isPdf ? new Blob([r.data], { type: "application/pdf" }) : r.data);
        setUrl(objectUrl);
      })
      .catch((e) => setError(e?.response?.status === 402 ? "Locked until the invoice is paid." : "Could not load preview."));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [file.id, isPdf]);

  return (
    <div className="relative w-full h-[65vh] rounded-xl overflow-hidden bg-slate-950 flex items-center justify-center" data-testid="file-preview-body">
      {error ? (
        <p className="text-sm text-red-400" data-testid="file-preview-error">{error}</p>
      ) : !url ? (
        <div className="h-8 w-8 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" data-testid="file-preview-loading" />
      ) : isPdf ? (
        <iframe src={url} title={file.filename} className="w-full h-full bg-white" />
      ) : (
        <img src={url} alt={file.filename} className="max-w-full max-h-full object-contain" />
      )}
    </div>
  );
}
